import { useMutation } from "react-query";
import { useContext } from "react";
import AuthContext from "contexts/AuthContext";
import { useTranslation } from "react-i18next";

const { REACT_APP_API } = process.env;

const updateUser = async ({ accessToken, payload, user }) => {
  const response = await fetch(`${REACT_APP_API}/user`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      ...payload,
      userEmail: user?.email,
      access_token: accessToken,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to update user");
  }

  return response.json();
};

export const useUpdateUser = ({
  onSuccess = () => ({}),
  onError = () => ({}),
} = {}) => {
  const { authTokens, user, refreshMutation, logoutHandler } =
    useContext(AuthContext);
  const { t } = useTranslation();
  const accessToken = authTokens?.access_token;

  return useMutation({
    mutationKey: ["update-user", accessToken],
    mutationFn: (payload) => updateUser({ accessToken, payload, user }),
    onSuccess: (data, variables) => {
      const wasEmailUpdated = variables.newEmail;
      const wasEmailOrPasswordUpdated = wasEmailUpdated || variables.password;

      // Trigger a refresh to get the latest user data
      if (authTokens?.refresh_token && user?.email) {
        refreshMutation(
          {
            refresh_token: authTokens.refresh_token,
            username: user.email,
          },
          {
            onSuccess: () => {
              onSuccess(data);
              // If email or password was updated, log the user out and redirect to login after 2s
              if (wasEmailOrPasswordUpdated) {
                setTimeout(() => {
                  logoutHandler(
                    wasEmailUpdated
                      ? `${t("urls.trade_in_verification")}?signUpOnly=true`
                      : t("urls.login")
                  );
                }, 2000);
              }
            },
            onError: (error) => {
              console.error("Refresh failed:", error);
              onSuccess(data); // Still call onSuccess with the update data
              // If email or password was updated, log the user out and redirect to login after 2s
              if (wasEmailOrPasswordUpdated) {
                setTimeout(() => {
                  logoutHandler(
                    wasEmailUpdated
                      ? `${t("urls.trade_in_verification")}?signUpOnly=true`
                      : t("urls.login")
                  );
                }, 2000);
              }
            },
          }
        );
      } else {
        onSuccess(data);
        // If email or password was updated, log the user out and redirect to login after 2s
        if (wasEmailOrPasswordUpdated) {
          setTimeout(() => {
            logoutHandler(t("urls.login"));
          }, 2000);
        }
      }
    },
    onError: (error) => {
      alert(error.message);
      onError(error);
    },
  });
};

export const useAccessToken = () => {
  const { authTokens } = useContext(AuthContext);
  return authTokens?.access_token;
};

// Helper function to get fresh token from localStorage
// This should be used inside fetch functions to avoid stale token issues
// DEPRECATED: Use useContext(AuthContext) instead for consistent token access
export const getFreshAccessToken = () => {
  console.warn(
    "getFreshAccessToken is deprecated. Use AuthContext tokens directly to avoid stale token issues."
  );
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));
  return authTokens?.access_token;
};
